/**
 * Cycle 408 — the effective level of the live alarm episode.
 *
 * `episodeLevel` used to be `currentLevel ?? startLevel`: the LIVE READING's
 * zone under the thresholds this device currently holds, falling back to
 * the level the episode opened at. Both halves are reading-derived. For the
 * first moments after a cold start from a critical alert those thresholds
 * are `DEFAULT_THRESHOLDS` (the member's settings have not loaded), so a
 * phone opened onto a real urgent low can classify the same reading as
 * `low` — and every control that reads `episodeLevel` then does the wrong
 * thing: the treating tap refuses locally, the LOW banner renders WITH a
 * Stop button, and `stop()` passes its guard (the cycle-73 floor, defeated
 * on the phone for a few seconds on exactly the alarm it protects).
 *
 * The server's own row for the episode carries the truth. Rule: the bound
 * row's level joins the derivation, and within the same family the MORE
 * SEVERE level wins (cycle 81's "a more-severe live reading still wins",
 * extended to the row). It can only ever make the surface more urgent,
 * never less: an in-range/quiet client signal is never overridden by a
 * bound row (no phantom episode while idle), and a bound row of the OTHER
 * family is ignored (a stale high row must not colour a low).
 */
import type { AlertLevel } from "./types";

const SEVERITY: Record<AlertLevel, number> = {
  low: 1,
  urgent_low: 2,
  high: 1,
  urgent_high: 2,
};

function isLowFamily(level: AlertLevel): boolean {
  return level === "low" || level === "urgent_low";
}

export function effectiveEpisodeLevel(
  currentLevel: AlertLevel | null,
  startLevel: AlertLevel | null,
  boundLevel: AlertLevel | null | undefined,
): AlertLevel | null {
  const base = currentLevel ?? startLevel;
  if (base == null) return null;
  if (boundLevel == null) return base;
  if (isLowFamily(boundLevel) !== isLowFamily(base)) return base;
  return SEVERITY[boundLevel] > SEVERITY[base] ? boundLevel : base;
}
